// Money: profit for the month, daily target, expenses, monthly bills, supplier bills (parts on credit).
import { useState } from 'react';
import { api, useApi, useLocation, setQuery, dollars, fmtDate, today, toCents, centsInput, downloadCsv, uploadPhoto, scanReceipt, type Row, openedForNew, payMethods } from '../lib';
import { Badge, Button, Panel, Table, Loading, ErrorBox, Modal, Form, Field, MoneyField, Select, TextArea, Tabs, Stat, Icon, Empty, useToast, confirmAction, ReceiptPicker, ReceiptThumb, StoreField, Link, PayMethodFields } from '../ui';
import { EXPENSE_CATEGORIES, PURCHASE_CATEGORIES } from '../../shared/calc';

const thisMonth = () => today().slice(0, 7);
const shiftMonth = (m: string, n: number) => { const [y, mo] = m.split('-').map(Number); const d = new Date(Date.UTC(y, mo - 1 + n, 1)); return d.toISOString().slice(0, 7); };
const monthName = (m: string) => new Date(m + '-15T12:00:00Z').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

export function Money() {
  const { query } = useLocation();
  const tab = query.get('tab') || 'overview';
  const month = query.get('month') || thisMonth();
  return (
    <>
      <div className="page-head">
        <div><h1>Money</h1><p>What came in, what went out, and what's left — by day and by month.</p></div>
        <div className="actions month-pick">
          <Button onClick={() => setQuery({ month: shiftMonth(month, -1) })} aria-label="Previous month"><Icon name="back" size={16} /></Button>
          <b>{monthName(month)}</b>
          <Button disabled={month >= thisMonth()} onClick={() => setQuery({ month: shiftMonth(month, 1) })} aria-label="Next month"><Icon name="back" size={16} className="flip" /></Button>
        </div>
      </div>
      <Tabs value={tab} onChange={t => setQuery({ tab: t === 'overview' ? '' : t })} tabs={[
        { id: 'overview', label: 'Profit' }, { id: 'expenses', label: 'Expenses' }, { id: 'purchases', label: 'Tools & supplies' }, { id: 'bills', label: 'Regular bills' }, { id: 'suppliers', label: 'Supplier bills' },
      ]} />
      {tab === 'overview' && <Overview month={month} />}
      {tab === 'expenses' && <Expenses month={month} />}
      {tab === 'purchases' && <Purchases month={month} />}
      {tab === 'bills' && <MonthlyBills month={month} />}
      {tab === 'suppliers' && <SupplierBills />}
    </>
  );
}

/** Today vs the daily target — also used on the dashboard. */
export function TodayTarget({ d }: { d: Row }) {
  const t = d.days.find((x: Row) => x.day === d.today) || { profit: 0, collected: 0, work: true };
  const target = d.costs.todayCost ?? d.costs.dailyTarget;
  const changes = (d.costs.todayChanges || []) as Row[];
  const diff = t.profit - target;
  const isToday = d.today >= d.start && d.today <= d.end;
  if (!isToday) return null;
  return (
    <div className={'target ' + (!t.work ? 'off' : diff >= 0 ? 'good' : 'short')}>
      <div>
        <small>Today</small>
        <strong>{dollars(t.profit)} <span>profit made</span></strong>
        <p>{!t.work ? 'Day off — no target today.'
          : diff >= 0 ? <>Today's costs ({dollars(target)}) are covered. <b>{dollars(diff)} profit</b> on top.</>
            : <>Need <b>{dollars(-diff)} more</b> to cover today's share of costs ({dollars(target)}).</>}</p>
      </div>
      <div className="target-side"><small>Collected today</small><b>{dollars(t.collected)}</b>
        {t.work && <small className="today-cost">Today's costs: bills {dollars(d.costs.billsPerDay)} + staff {dollars(d.costs.todayStaff)}
          {changes.map((x, i) => <span key={i} className={x.amount < 0 ? 'cost-down' : 'cost-up'}>{x.name} {x.status || ''} {x.amount < 0 ? '−' : '+'}{dollars(Math.abs(x.amount))}</span>)}</small>}
      </div>
    </div>
  );
}

export function MonthProgress({ d }: { d: Row }) {
  const pct = d.costs.monthCosts ? Math.min(100, Math.round((d.income.grossProfit / d.costs.monthCosts) * 100)) : 100;
  const covered = d.income.grossProfit >= d.costs.monthCosts;
  return (
    <div className="progress-block">
      <div className="progress-head">
        <span>{covered ? 'Month costs covered' : 'Covering this month\'s costs'}</span>
        <b>{dollars(d.income.grossProfit)} of {dollars(d.costs.monthCosts)}</b>
      </div>
      <div className="meter" role="img" aria-label={`${pct}% of monthly costs covered`}><i style={{ width: pct + '%' }} className={covered ? 'good' : ''} /></div>
      <small className="muted">{covered ? `${dollars(d.income.grossProfit - d.costs.monthCosts)} above costs` : `${dollars(d.leftToCover)} left to cover · ${d.costs.workDays - d.costs.workDaysSoFar} work days left`}</small>
    </div>
  );
}

function Overview({ month }: { month: string }) {
  const { data: d, error, reload } = useApi<Row>(`/api/money/overview?month=${month}`, [month]);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!d) return <Loading />;
  const i = d.income, c = d.costs;
  const maxBar = Math.max(c.dailyTarget, ...d.days.map((x: Row) => x.profit), 1);
  return (
    <>
      <TodayTarget d={d} />
      <div className="stats">
        <Stat label={d.toDate < d.end ? 'Net profit so far' : 'Net profit'} value={<span className={d.netProfit < 0 ? 'danger' : ''}>{dollars(d.netProfit)}</span>} hint="After parts, staff, bills and expenses" tone={d.netProfit < 0 ? 'warn' : ''} />
        <Stat label="Gross profit from jobs" value={dollars(i.grossProfit)} hint={`${i.invoices} invoices`} />
        <Stat label="Daily target" value={dollars(c.dailyTarget)} hint={`${dollars(c.monthCosts)} ÷ ${c.workDays} work days`} />
        <Stat label="Collected" value={dollars(i.collected)} hint="Payments received this month" />
      </div>
      <Panel><div className="pad"><MonthProgress d={d} /></div></Panel>

      <div className="grid-2">
        <Panel title={`Profit & loss — ${monthName(month)}`}>
          <dl className="totals pl">
            <dt>Labor</dt><dd>{dollars(i.labor)}</dd>
            {i.misc > 0 && <><dt>Misc / fees</dt><dd>{dollars(i.misc)}</dd></>}
            <dt>Shop supplies charged</dt><dd>{dollars(i.supplies)}</dd>
            <dt>Parts sold</dt><dd>{dollars(i.partsSold)}</dd>
            <dt className="muted">− What those parts cost you</dt><dd className="muted">−{dollars(i.partsCost)}</dd>
            <dt className="grand">Gross profit</dt><dd className="grand">{dollars(i.grossProfit)}</dd>
            <dt>− Staff pay {d.toDate < d.end ? '(so far)' : ''}</dt><dd>−{dollars(c.staffSoFar)}</dd>
            <dt>− Regular bills (rent, utilities, food…)</dt><dd>−{dollars(c.fixed)}</dd>
            <dt>− Other expenses (tools, supplies, one-offs)</dt><dd>−{dollars(c.other)}</dd>
            <dt className="grand">Net profit</dt><dd className={'grand ' + (d.netProfit < 0 ? 'danger' : '')}>{dollars(d.netProfit)}</dd>
          </dl>
          <p className="form-note pad">Sales tax collected ({dollars(i.tax)}) is not counted — it belongs to the state. Parts bought this month ({dollars(c.supplierPaid)} paid to suppliers) are counted when they're used on a job, not when bought.</p>
        </Panel>
        <div className="stack">
          <ShelfMoney d={d} />
          <Panel title="Staff pay this month">
            <Table heads={['Name', 'Pay', 'Amount']} empty="No paid staff yet."
              rows={(d.staffPay as Row[]).map(s => [s.name, <small>{s.pay_type.replace('_', ' ')}</small>, dollars(s.amount)])} />
            <p className="form-note pad">Hourly staff from the time clock; salaries for the days so far, including attendance marks. Full-month estimate: {dollars(c.staffEstimate)}.</p>
          </Panel>
        </div>
      </div>

      <Panel title="Profit by day" action={<span className="legend-inline"><i className="bar-good" />Above target <i className="bar-short" />Below target <i className="line" />Daily target</span>}>
        <div className="daybars" role="img" aria-label="Gross profit for each day of the month compared with the daily target">
          <div className="target-line" style={{ bottom: `calc(${(c.dailyTarget / maxBar) * 100}% * 0.86 + 22px)` }}><span>{dollars(c.dailyTarget)}</span></div>
          {(d.days as Row[]).map(x => (
            <div key={x.day} className={'daybar' + (x.work ? '' : ' off') + (x.day === d.today ? ' today' : '')} title={`${fmtDate(x.day)}: ${dollars(x.profit)} profit, ${dollars(x.collected)} collected`}>
              <i style={{ height: `${(x.profit / maxBar) * 86}%` }} className={x.profit >= c.dailyTarget ? 'good' : 'short'} />
              <small>{Number(x.day.slice(8))}</small>
            </div>
          ))}
        </div>
      </Panel>
    </>
  );
}

export function ShelfMoney({ d }: { d: Row }) {
  return (
    <Panel title="Money tied up in parts">
      <dl className="totals">
        <dt>Parts on your shelves (at cost)</dt><dd><b>{dollars(d.inventory.value)}</b></dd>
        <dt>You owe suppliers</dt><dd className={d.credit.owed ? 'warn-text' : ''}><b>{dollars(d.credit.owed)}</b></dd>
        {d.credit.overdue > 0 && <><dt className="danger">… past the pay-by date</dt><dd className="danger">{dollars(d.credit.overdue)}</dd></>}
      </dl>
      {d.credit.usedUnpaid > 0 && (
        <div className="note-bar warn mx"><Icon name="alert" size={16} />
          <span><b>{dollars(d.credit.usedUnpaid)}</b> of parts you haven't paid for are already installed on customer trucks. Keep that money aside for the supplier.</span>
        </div>
      )}
    </Panel>
  );
}

/** Badges for receipts sent from the phone and money a worker paid out of pocket. */
function ExpenseTags({ e }: { e: Row }) {
  return <>
    {e.recurring_id && <Badge tone="blue">Monthly</Badge>}
    {e.status === 'pending' && <Badge tone="amber">Waiting for OK</Badge>}
    {e.paid_by === 'worker' && (e.reimbursed_on ? <Badge tone="green">{e.paid_by_name} paid · paid back</Badge> : <Badge tone="red">{e.paid_by_name} paid · owe back</Badge>)}
  </>;
}

function ExpenseActions({ e, onEdit, reload }: { e: Row; onEdit: () => void; reload: () => void }) {
  const toast = useToast();
  return (
    <span className="row-actions">
      {e.status === 'pending' && <Button kind="primary" onClick={async () => { await api(`/api/expenses/${e.id}/approve`, { body: {} }); toast('Approved — counted in this month'); reload(); }}>OK</Button>}
      {e.status !== 'pending' && e.paid_by === 'worker' && !e.reimbursed_on && <Button kind="ghost" onClick={async () => { await api(`/api/expenses/${e.id}/reimburse`, { body: {} }); toast(`Marked paid back to ${e.paid_by_name}`); reload(); }}>Paid back</Button>}
      {!e.recurring_id && <button className="icon-btn" title="Edit" onClick={onEdit}><Icon name="edit" size={16} /></button>}
      <button className="icon-btn" title="Delete" onClick={async () => { if (confirmAction('Delete this expense?')) { await api(`/api/expenses/${e.id}`, { method: 'DELETE' }); reload(); } }}><Icon name="trash" size={16} /></button>
    </span>
  );
}

function Expenses({ month }: { month: string }) {
  const { data: d, error, reload } = useApi<Row>(`/api/money/overview?month=${month}`, [month]);
  const [modal, setModal] = useState<null | 'add' | { edit: Row }>(() => (openedForNew() ? 'add' : null));
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!d) return <Loading />;
  const list = d.expenses as Row[];
  const counted = list.filter(e => e.status !== 'pending');
  const byCat: Record<string, number> = {};
  for (const e of counted) byCat[e.category] = (byCat[e.category] || 0) + e.amount;
  const spent = counted.reduce((a, e) => a + e.amount, 0);
  const staffSoFar = d.costs.staffSoFar as number;
  return (
    <>
      <div className="toolbar"><span className="muted">{counted.length} expenses {dollars(spent)} + staff pay {dollars(staffSoFar)} = <b>{dollars(spent + staffSoFar)}</b> spent{d.toDate < d.end ? ' so far' : ''}</span><span className="spacer" />
        <Button icon="download" onClick={() => downloadCsv(`expenses-${month}.csv`, ['Date', 'Category', 'Paid to', 'Description', 'Amount', 'Paid with', 'Paid by', 'Status'], list.map(e => [e.spent_on, e.category, e.vendor, e.description, e.amount / 100, e.method, e.paid_by === 'worker' ? e.paid_by_name : 'Shop', e.status]))}>CSV</Button>
        <Button kind="primary" icon="plus" onClick={() => setModal('add')}>Add expense</Button></div>
      <div className="grid-2 wide-left">
        <Panel>
          <Table heads={['Date', 'Category', 'Paid to / for', 'Paid with', 'Receipt', 'Amount', '']} empty="No expenses this month. Add rent, supplies, bills as you pay them."
            rows={list.map(e => [fmtDate(e.spent_on), <>{e.category}<span className="tags"><ExpenseTags e={e} /></span></>, <><b>{e.vendor || '—'}</b><small className="muted">{e.description}{e.created_by_name && e.status === 'pending' ? ` · sent by ${e.created_by_name}` : ''}</small></>, e.method || '—',
              <ReceiptThumb id={e.photo_id} />, <b className={e.status === 'pending' ? 'muted' : ''}>{dollars(e.amount)}</b>,
              <ExpenseActions e={e} onEdit={() => setModal({ edit: e })} reload={reload} />])} />
        </Panel>
        <div className="stack">
          <Panel title="By category">
            <dl className="totals">{Object.entries(byCat).sort((a, b) => b[1] - a[1]).flatMap(([k, val]) => [<dt key={k + 't'}>{k}</dt>, <dd key={k + 'd'}>{dollars(val)}</dd>])}
              <dt>Staff pay <Badge tone="blue">auto</Badge></dt><dd>{dollars(staffSoFar)}</dd>
              <dt className="grand">Total</dt><dd className="grand">{dollars(spent + staffSoFar)}</dd></dl>
          </Panel>
          <StaffPayPanel d={d} />
        </div>
      </div>
      {modal && <ExpenseModal edit={modal === 'add' ? undefined : modal.edit} onClose={() => setModal(null)} onDone={() => { setModal(null); reload(); }} />}
    </>
  );
}

export function ExpenseModal({ edit, store, onClose, onDone }: { edit?: Row; store?: boolean; onClose: () => void; onDone: () => void }) {
  const staff = useApi<Row[]>('/api/users');
  const [file, setFile] = useState<File | null>(null);
  const [by, setBy] = useState<string>(edit?.paid_by || 'shop');
  const [pre, setPre] = useState<Row | null>(null);
  const [reading, setReading] = useState(false);
  const toast = useToast();
  const e0: Row = edit || (pre ? { amount: pre.total, spent_on: pre.date, category: pre.category || undefined, vendor: pre.store, description: pre.description } : {});
  return (
    <Modal title={edit ? 'Edit expense' : store ? 'Store receipt' : 'Add expense'} onClose={onClose}>
      {!edit && file && !pre && <div className="ai-fill"><Button kind="primary" busy={reading} onClick={async () => {
        setReading(true);
        try { const r = await scanReceipt(file); setPre(r); toast('Filled in from the photo — check it and save'); } catch (e) { toast((e as Error).message, 'err'); } finally { setReading(false); }
      }}>✨ Fill in from the photo (AI)</Button></div>}
      <Form key={pre ? 'ai' : 'manual'} onCancel={onClose} onSubmit={async v => {
        const photo_id = pre?.photo_id || (file ? (await uploadPhoto(file, { kind: 'receipt', purpose: 'expense' })).id : undefined);
        const body = { ...v, amount: toCents(v.amount), ...(photo_id ? { photo_id } : {}) };
        if (!edit) await api('/api/expenses', { body }); else await api(`/api/expenses/${edit.id}`, { method: 'PATCH', body });
        toast('Expense saved'); onDone();
      }}>
        <MoneyField label="Amount" name="amount" required defaultValue={e0.amount ? centsInput(e0.amount) : ''} autoFocus />
        <Field label="Date" name="spent_on" type="date" required defaultValue={e0.spent_on || today()} />
        <Select label="Category" name="category" required defaultValue={e0.category || (store ? 'Tools & equipment' : 'Shop supplies')}>
          {(store ? PURCHASE_CATEGORIES : EXPENSE_CATEGORIES).map(c => <option key={c}>{c}</option>)}</Select>
        <Select label="Paid with" name="method" defaultValue={edit ? edit.method : 'Card'}><option value="">—</option>{payMethods().map(m => <option key={m}>{m}</option>)}</Select>
        {store ? <StoreField defaultValue={e0.vendor || ''} required full={false} />
          : <Field label="Paid to" name="vendor" defaultValue={e0.vendor || ''} placeholder="Harbor Freight, Duke Energy…" />}
        <Select label="Who paid" name="paid_by" value={by} onChange={e => setBy(e.target.value)}><option value="shop">Shop money (card / cash)</option><option value="worker">A worker, with own money</option></Select>
        {by === 'worker' && <Select label="Which worker" name="paid_by_user" key={staff.data ? "l" : "n"} defaultValue={edit?.paid_by_user || ''} required hint="Shows as owed until you mark it paid back">
          <option value="">Choose…</option>{(staff.data || []).filter(u => u.active).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</Select>}
        <TextArea label="What for" name="description" defaultValue={e0.description || ''} placeholder={store ? 'e.g. 2 cut-off wheels, sawzall blades, degreaser' : ''} />
        <ReceiptPicker file={file} onChange={setFile} current={edit?.photo_id} />
        {!store && <p className="form-note full">Rent, internet, Cintas, food and other bills that repeat go under <b>Regular bills</b>. Parts you stock go through <b>Inventory → Receive</b>.</p>}
      </Form>
    </Modal>
  );
}

/** Store runs — tools, blades, cleaning stuff: where that money goes, month by month. */
function Purchases({ month }: { month: string }) {
  const { data: d, error, reload } = useApi<Row>(`/api/money/purchases?month=${month}`, [month]);
  const [modal, setModal] = useState<null | 'add' | { edit: Row }>(() => (openedForNew() ? 'add' : null));
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!d) return <Loading />;
  const cats = d.categories as string[];
  const cell = (m: string, c?: string) => (d.byMonth as Row[]).filter(x => x.month === m && (!c || x.category === c)).reduce((a, x) => a + x.total, 0);
  const thisMonth = cell(month);
  const year = (d.months as string[]).reduce((a, m) => a + cell(m), 0);
  const pending = d.pending as Row[], owed = d.owed as Row[], list = d.list as Row[];
  const usedCats = cats.filter(c => (d.months as string[]).some(m => cell(m, c)));
  const short = (m: string) => new Date(m + '-15T12:00:00Z').toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  return (
    <>
      <div className="stats">
        <Stat label={`Tools & supplies — ${monthName(month)}`} value={dollars(thisMonth)} hint={`${list.length} receipts`} />
        <Stat label="Last 12 months" value={dollars(year)} hint={`about ${dollars(Math.round(year / 12))} a month`} />
        <Stat label="Waiting for your OK" value={pending.length} hint={pending.length ? dollars(pending.reduce((a, e) => a + e.amount, 0)) + ' sent from phones' : 'Nothing waiting'} tone={pending.length ? 'warn' : ''} />
        <Stat label="Owed back to workers" value={dollars(owed.reduce((a, e) => a + e.amount, 0))} hint="They paid with their own money" tone={owed.length ? 'warn' : ''} />
      </div>
      <div className="toolbar"><span className="muted">Harbor Freight, Home Depot runs — blades, bits, cleaning stuff, broken tools replaced. Mechanics can send receipts from their phone.</span><span className="spacer" />
        <Button kind="primary" icon="camera" onClick={() => setModal('add')}>Add store receipt</Button></div>

      {pending.length > 0 && (
        <Panel title="Sent from phones — check and OK">
          <Table heads={['Receipt', 'Date', 'Sent by', 'Store / what', 'Category', 'Paid by', 'Amount', '']}
            rows={pending.map(e => [<ReceiptThumb id={e.photo_id} />, fmtDate(e.spent_on), e.created_by_name, <><b>{e.vendor}</b><small className="muted">{e.description}</small></>, e.category,
              e.paid_by === 'worker' ? <Badge tone="red">Own money</Badge> : 'Shop', <b>{dollars(e.amount)}</b>, <ExpenseActions e={e} onEdit={() => setModal({ edit: e })} reload={reload} />])} />
        </Panel>
      )}
      {owed.length > 0 && (
        <Panel title="Pay these back">
          <Table heads={['Receipt', 'Date', 'Worker', 'Store / what', 'Amount', '']}
            rows={owed.map(e => [<ReceiptThumb id={e.photo_id} />, fmtDate(e.spent_on), <b>{e.paid_by_name}</b>, <><b>{e.vendor}</b><small className="muted">{e.description}</small></>, <b>{dollars(e.amount)}</b>,
              <ExpenseActions e={e} onEdit={() => setModal({ edit: e })} reload={reload} />])} />
        </Panel>
      )}

      <Panel title="Month by month">
        <div className="month-scroll">
          <table className="month-grid">
            <thead><tr><th>Month</th>{usedCats.map(c => <th key={c}>{c}</th>)}<th>Total</th></tr></thead>
            <tbody>{(d.months as string[]).slice().reverse().map(m => (
              <tr key={m} className={m === month ? 'current' : ''}><td><b>{short(m)}</b></td>{usedCats.map(c => <td key={c}>{cell(m, c) ? dollars(cell(m, c)) : <span className="muted">—</span>}</td>)}<td><b>{dollars(cell(m))}</b></td></tr>
            ))}</tbody>
          </table>
        </div>
      </Panel>

      <div className="grid-2 wide-left">
        <Panel title={`Receipts — ${monthName(month)}`}>
          <Table heads={['Receipt', 'Date', 'Store / what', 'Category', 'Amount', '']} empty="No store receipts this month."
            rows={list.map(e => [<ReceiptThumb id={e.photo_id} />, fmtDate(e.spent_on), <><b>{e.vendor || '—'}</b><small className="muted">{e.description}{e.created_by_name ? ` · ${e.created_by_name}` : ''}</small></>,
              <>{e.category}<span className="tags"><ExpenseTags e={e} /></span></>, <b>{dollars(e.amount)}</b>, <ExpenseActions e={e} onEdit={() => setModal({ edit: e })} reload={reload} />])} />
        </Panel>
        <Panel title="By store">
          {(d.stores as Row[]).length ? <dl className="totals">{(d.stores as Row[]).flatMap(x => [<dt key={x.vendor + 't'}>{x.vendor} <small className="muted">· {x.n}×</small></dt>, <dd key={x.vendor + 'd'}>{dollars(x.total)}</dd>])}</dl> : <Empty>Nothing yet.</Empty>}
        </Panel>
      </div>
      {modal && <ExpenseModal store edit={modal === 'add' ? undefined : modal.edit} onClose={() => setModal(null)} onDone={() => { setModal(null); reload(); }} />}
    </>
  );
}

/** Staff pay comes from Staff & Payroll automatically — shown here so it's clear it is counted (don't add it as an expense too). */
function StaffPayPanel({ d, plan }: { d: Row; plan?: boolean }) {
  const so = d.staffPay as Row[], pl = (d.staffPlan || []) as Row[];
  return (
    <Panel title="Staff pay — counted automatically" action={<Link to="/staff?tab=payroll">Staff & Payroll →</Link>}>
      <Table heads={plan ? ['Person', 'Pay', 'Per month'] : ['Person', 'Pay', d.toDate < d.end ? 'So far' : 'This month']} empty="No one on payroll."
        rows={plan ? pl.map(s => [s.name, <small>{s.pay_type.replace('_', ' ')}</small>, s.monthly ? dollars(s.monthly) : <small className="muted">by jobs / time clock</small>])
          : so.map(s => [s.name, <small>{s.pay_type.replace('_', ' ')}</small>, dollars(s.amount)])} />
      <p className="form-note pad">Comes from time clock, salaries and attendance (absent days come off). It's already in your costs, profit and daily target — <b>don't add payroll as an expense</b>, or it's counted twice.</p>
    </Panel>
  );
}

function MonthlyBills({ month }: { month: string }) {
  const { data: d, error, reload } = useApi<Row>(`/api/money/overview?month=${month}`, [month]);
  const [modal, setModal] = useState<null | 'add' | { edit: Row } | { pay: Row }>(null);
  const toast = useToast();
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!d) return <Loading />;
  const list = d.recurring as Row[];
  const total = list.reduce((a, r) => a + r.monthly, 0);
  const unpaid = list.filter(r => r.frequency === 'monthly' && !r.paid);
  return (
    <>
      <div className="stats">
        <Stat label="Regular bills per month" value={dollars(total)} hint={`${list.length} bills · weekly and daily ones converted`} />
        <Stat label={`Not paid yet for ${monthName(month)}`} value={dollars(unpaid.reduce((a, r) => a + r.amount, 0))} tone={unpaid.length ? 'warn' : ''} hint={unpaid.map(u => u.name).join(', ') || 'All paid'} />
        <Stat label="Per work day" value={dollars(Math.round(total / Math.max(1, d.costs.workDays)))} hint="Before staff pay" />
        <Stat label="Staff pay per month" value={dollars(d.costs.staffEstimate)} hint={`Automatic from Staff & Payroll · bills + staff = ${dollars(total + d.costs.staffEstimate)}`} />
      </div>
      <div className="toolbar"><span className="muted">These count toward every month's costs automatically. Weekly bills × 52 ÷ 12; daily ones × work days.</span><span className="spacer" /><Button kind="primary" icon="plus" onClick={() => setModal('add')}>Add regular bill</Button></div>
      <Panel>
        <Table heads={['Bill', 'Category', 'How often', 'Amount', 'Per month', `${monthName(month)}`, '']} empty="Add rent, internet, insurance, phone… anything you pay regularly."
          rows={list.map(r => [<><b>{r.name}</b><small className="muted">{r.vendor}</small></>, r.category,
            r.frequency === 'monthly' ? `Monthly · day ${r.day_of_month}` : r.frequency === 'weekly' ? 'Weekly' : 'Every work day', dollars(r.amount), <b>{dollars(r.monthly)}</b>,
            r.frequency !== 'monthly' ? <span className="muted">Counted</span> : r.paid ? <Badge tone="green">Paid</Badge> : <Button kind="ghost" onClick={() => setModal({ pay: r })}>Mark paid</Button>,
            <button className="icon-btn" title="Edit" onClick={() => setModal({ edit: r })}><Icon name="edit" size={16} /></button>])} />
      </Panel>
      <StaffPayPanel d={d} plan />
      {(modal === 'add' || (typeof modal === 'object' && modal && 'edit' in modal)) && (() => {
        const r = typeof modal === 'object' ? (modal as { edit: Row }).edit : undefined;
        return (
          <Modal title={r ? `Edit ${r.name}` : 'Add regular bill'} onClose={() => setModal(null)}>
            <Form onCancel={() => setModal(null)} onSubmit={async v => {
              const body = { ...v, amount: toCents(v.amount), day_of_month: Number(v.day_of_month || 1), active: r ? v.active === 'on' : true };
              if (r) await api(`/api/recurring/${r.id}`, { method: 'PATCH', body }); else await api('/api/recurring', { body });
              setModal(null); reload();
            }}>
              <Field label="Name" name="name" required defaultValue={r?.name} placeholder="Shop rent" />
              <MoneyField label="Amount" name="amount" required defaultValue={centsInput(r?.amount)} />
              <Select label="How often" name="frequency" defaultValue={r?.frequency || 'monthly'}><option value="monthly">Every month</option><option value="weekly">Every week</option><option value="daily">Every work day</option></Select>
              <Select label="Category" name="category" defaultValue={r?.category || 'Rent'}>{EXPENSE_CATEGORIES.map(c => <option key={c}>{c}</option>)}</Select>
              <Field label="Due day (monthly bills)" name="day_of_month" type="number" min={1} max={31} defaultValue={r?.day_of_month || 1} />
              <Field label="Paid to" name="vendor" full defaultValue={r?.vendor} placeholder="Landlord, Spectrum, Duke Energy…" />
              {r && <label className="check full"><input type="checkbox" name="active" defaultChecked={!!r.active} /> Still paying this every month</label>}
            </Form>
          </Modal>
        );
      })()}
      {typeof modal === 'object' && modal && 'pay' in modal && (
        <Modal title={`Pay ${modal.pay.name} — ${monthName(month)}`} onClose={() => setModal(null)}>
          <Form submitLabel="Mark paid" onCancel={() => setModal(null)} onSubmit={async v => {
            await api(`/api/recurring/${modal.pay.id}/pay`, { body: { month, spent_on: v.spent_on, method: v.method, amount: toCents(v.amount) } });
            setModal(null); toast('Marked paid'); reload();
          }}>
            <MoneyField label="Amount" name="amount" defaultValue={centsInput(modal.pay.amount)} required />
            <Field label="Date paid" name="spent_on" type="date" defaultValue={today()} required />
            <Select label="Paid with" name="method"><option value="">—</option>{payMethods().map(m => <option key={m}>{m}</option>)}</Select>
          </Form>
        </Modal>
      )}
    </>
  );
}

function SupplierBills() {
  const [status, setStatus] = useState('open');
  const { data: d, error, reload } = useApi<Row>(`/api/vendor-bills?status=${status}`, [status]);
  const [open, setOpen] = useState<string | null>(null);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!d) return <Loading />;
  return (
    <>
      <div className="stats">
        <Stat label="You owe suppliers" value={dollars(d.owed)} tone={d.owed ? 'warn' : ''} />
        <Stat label="Already installed, not paid" value={dollars(d.usedUnpaid)} tone={d.usedUnpaid ? 'warn' : ''} hint="Set this money aside" />
        <Stat label="Past pay-by date" value={dollars((d.bills as Row[]).filter(b => b.overdue).reduce((a, b) => a + b.balance, 0))} />
      </div>
      <div className="toolbar">
        <select value={status} onChange={e => setStatus(e.target.value)} aria-label="Show"><option value="open">Not paid</option><option value="paid">Paid</option><option value="all">All</option></select>
        <span className="muted">Bills are created when you receive parts in Inventory (paid now or on credit).</span>
      </div>
      <Panel>
        <Table heads={['Supplier', 'Invoice #', 'Received', 'Pay by', 'Total', 'Owed', 'Installed, unpaid', '']} empty={status === 'open' ? 'Nothing owed to suppliers.' : 'No bills.'}
          rows={(d.bills as Row[]).map(b => [<b>{b.supplier}</b>, b.reference || '—', fmtDate(b.bill_date), <span className={b.overdue ? 'danger' : ''}>{fmtDate(b.due_date)}</span>, dollars(b.amount),
            <b className={b.balance > 0 ? 'warn-text' : ''}>{dollars(b.balance)}</b>, b.used_unpaid ? <span className="danger">{dollars(b.used_unpaid)}</span> : '—',
            b.balance > 0 ? <Badge tone={b.overdue ? 'red' : 'amber'}>{b.overdue ? 'Overdue' : 'Open'}</Badge> : <Badge tone="green">Paid</Badge>])}
          onRow={i => setOpen(d.bills[i].id)} />
      </Panel>
      {open && <BillModal id={open} onClose={() => { setOpen(null); reload(); }} />}
    </>
  );
}

function BillModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, reload } = useApi<Row>(`/api/vendor-bills/${id}`);
  const [paying, setPaying] = useState(false);
  const toast = useToast();
  if (!data) return <Modal title="Supplier bill" onClose={onClose}><Loading /></Modal>;
  const b = data.bill as Row;
  const bal = b.amount - b.paid;
  return (
    <Modal title={`${b.supplier}${b.reference ? ' · #' + b.reference : ''}`} onClose={onClose} wide>
      {!paying ? (
        <div className="form">
          <div className="pad">
            <div className="kv flat"><div><small>Received</small><b>{fmtDate(b.bill_date)}</b></div><div><small>Pay by</small><b>{fmtDate(b.due_date)}</b></div><div><small>Owed</small><b className={bal ? 'warn-text' : ''}>{dollars(bal)} of {dollars(b.amount)}</b></div></div>
          </div>
          <Table heads={['Part', 'Qty', 'Cost each', 'Amount', 'On shelf now']} rows={(data.items as Row[]).map(i => [<><b>{i.name}</b><small className="muted">{i.sku}</small></>, i.qty, dollars(i.unit_cost), dollars(i.qty * i.unit_cost), i.stock])} />
          <Table heads={['Paid on', 'Method', 'Reference', 'Amount']} empty="No payments yet." rows={(data.payments as Row[]).map(p => [fmtDate(p.paid_on), p.method || '—', p.reference || '—', dollars(p.amount)])} />
          <div className="form-foot"><span className="spacer" /><Button onClick={onClose}>Close</Button>{bal > 0 && <Button kind="primary" icon="dollar" onClick={() => setPaying(true)}>Pay supplier</Button>}</div>
        </div>
      ) : (
        <Form submitLabel="Record payment" onCancel={() => setPaying(false)} onSubmit={async v => {
          await api(`/api/vendor-bills/${id}/payments`, { body: { amount: toCents(v.amount), paid_on: v.paid_on, method: v.method, reference: v.reference } });
          setPaying(false); toast('Supplier payment recorded'); reload();
        }}>
          <MoneyField label="Amount" name="amount" required defaultValue={centsInput(bal)} hint={`Owed ${dollars(bal)}`} />
          <Field label="Date paid" name="paid_on" type="date" defaultValue={today()} required />
          <PayMethodFields methods={payMethods()} label="Paid with" blank />
        </Form>
      )}
    </Modal>
  );
}
